import React, { useState, useEffect } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import axios from "axios";
import { baseurl } from "../../baseurl";
import { FaEye, FaFileInvoice, FaSearch, FaFilter, FaArrowRight, FaDownload } from "react-icons/fa";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";

function AllInvoice() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    fromDate: "",
    toDate: "",
  });

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        status: filters.status || undefined,
        search: filters.search || undefined,
        start_date: filters.fromDate ? filters.fromDate.toISOString().split("T")[0] : undefined,
        end_date: filters.toDate ? filters.toDate.toISOString().split("T")[0] : undefined,
      };

      const res = await axios.get(`${baseurl}getAllOrdersForAdmin`, { params });
      if (res.data.success) {
        setOrders(res.data.orders || []);
        setTotalCount(res.data.count || 0);
      }
    } catch (err) {
      console.error("Fetch orders error:", err);
      Swal.fire("Error", "Failed to fetch invoices", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [filters.status, filters.fromDate, filters.toDate, page, limit]);

  useEffect(() => {
    const delay = setTimeout(() => {
      setPage(1);
      fetchOrders();
    }, 500);
    return () => clearTimeout(delay);
  }, [filters.search]);

  return (
    <>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper bg-light">
        <div className="main-content p-3 p-lg-4">
          
          {/* Page Header */}
          <div className="row mb-4 align-items-end">
            <div className="col">
              <h4 className="fw-bold mb-1">Invoice Central</h4>
              <p className="text-muted small mb-0">Review, track, and export billing documents for all platform transactions.</p>
            </div>
            <div className="col-auto">
                <button className="btn btn-sm btn-white border shadow-sm px-3 fw-bold text-primary">
                    <FaDownload size={12} className="me-2" /> Export CSV
                </button>
            </div>
          </div>

          {/* Business Insights (Informative UI) */}
          <div className="row g-3 mb-4">
            <div className="col-md-3">
              <div className="card border-0 shadow-sm p-3 rounded-3 border-start border-primary border-4">
                <small className="text-muted text-uppercase fw-bold extra-small">Total Invoiced</small>
                <h5 className="mb-0 fw-bold">₹{orders.reduce((acc, curr) => acc + (curr.total_amount || 0), 0).toLocaleString()}</h5>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card border-0 shadow-sm p-3 rounded-3 border-start border-success border-4">
                <small className="text-muted text-uppercase fw-bold extra-small">Paid Documents</small>
                <h5 className="mb-0 fw-bold text-success">{orders.filter(o => o.payment_status === "Paid").length} Items</h5>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card border-0 shadow-sm p-3 rounded-3 border-start border-warning border-4">
                <small className="text-muted text-uppercase fw-bold extra-small">Pending Settlement</small>
                <h5 className="mb-0 fw-bold text-warning">{orders.filter(o => o.payment_status !== "Paid").length} Items</h5>
              </div>
            </div>
          </div>

          {/* Advanced Filter Toolbar */}
          <div className="card border-0 shadow-sm rounded-3 mb-4 overflow-hidden">
            <div className="card-body p-2 bg-white">
              <div className="row g-2">
                <div className="col-md-4">
                  <div className="input-group input-group-sm">
                    <span className="input-group-text bg-light border-0"><FaSearch /></span>
                    <input
                      type="text"
                      className="form-control bg-light border-0"
                      placeholder="Invoice ID, Email, Phone..."
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    />
                  </div>
                </div>
                <div className="col-md-2">
                  <select
                    className="form-select form-select-sm border-0 bg-light"
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  >
                    <option value="">Order Status</option>
                    <option value="Delivered">Delivered</option>
                    <option value="Shipped">Shipped</option>
                    <option value="Pending">Pending</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <div className="d-flex gap-1">
                    <Flatpickr
                      placeholder="Start Date"
                      className="form-control form-control-sm border-0 bg-light"
                      value={filters.fromDate}
                      onChange={(date) => setFilters({ ...filters, fromDate: date[0] })}
                    />
                    <div className="align-self-center px-1 text-muted">→</div>
                    <Flatpickr
                      placeholder="End Date"
                      className="form-control form-control-sm border-0 bg-light"
                      value={filters.toDate}
                      onChange={(date) => setFilters({ ...filters, toDate: date[0] })}
                    />
                  </div>
                </div>
                <div className="col-md-2">
                    <button className="btn btn-sm btn-primary w-100 fw-bold py-2" onClick={fetchOrders}>Apply Filters</button>
                </div>
              </div>
            </div>
          </div>

          {/* Compact Invoice Table */}
          <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
            <div className="table-responsive">
              <table className="table align-middle table-hover mb-0 invoice-table">
                <thead className="bg-light">
                  <tr>
                    <th className="ps-4">Document</th>
                    <th>Associated Vendors</th>
                    <th>Value</th>
                    <th>Compliance</th>
                    <th>Billing Date</th>
                    <th className="text-end pe-4">Access</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="6" className="text-center py-5">
                        <div className="spinner-border spinner-border-sm text-primary"></div>
                    </td></tr>
                  ) : orders.map((o, i) => (
                    <tr key={o.order_id}>
                      <td className="ps-4">
                        <div className="d-flex align-items-center">
                            <div className="invoice-icon-box me-3"><FaFileInvoice /></div>
                            <div>
                                <div className="fw-bold text-dark mb-0 font-monospace" style={{fontSize: '13px'}}>#{o.order_uuid.substring(0, 8)}</div>
                                <div className="extra-small text-muted text-uppercase">{o.payment_method}</div>
                            </div>
                        </div>
                      </td>
                      <td>
                        <div className="vendor-stack">
                            {o.vendors?.map((v, idx) => (
                                <div key={idx} className="small text-truncate" style={{maxWidth: '180px'}}>
                                    <span className="text-primary fw-semibold">•</span> {v.business_name}
                                </div>
                            ))}
                        </div>
                      </td>
                      <td>
                        <div className="fw-bold text-dark">₹{o.total_amount?.toLocaleString()}</div>
                        <span className={`status-pill ${o.payment_status === "Paid" ? "paid" : "unpaid"}`}>
                            {o.payment_status}
                        </span>
                      </td>
                      <td>
                        <span className={`badge-soft ${o.order_status === "Delivered" ? "bg-success" : "bg-warning"}`}>
                           {o.order_status}
                        </span>
                      </td>
                      <td>
                        <div className="small fw-bold">{new Date(o.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</div>
                        <div className="extra-small text-muted">{new Date(o.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                      </td>
                      <td className="text-end pe-4">
                        <Link to={`/order-view/${o.order_uuid}`} className="btn btn-view-invoice">
                          View <FaArrowRight className="ms-1" size={10} />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="p-3 bg-white border-top d-flex justify-content-between align-items-center">
              <span className="text-muted extra-small">Records { (page - 1) * limit + 1 } to { Math.min(page * limit, totalCount) } of {totalCount}</span>
              <div className="btn-group shadow-sm">
                <button className="btn btn-sm btn-white border px-3" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</button>
                <button className="btn btn-sm btn-white border px-3" disabled={page >= Math.ceil(totalCount / limit)} onClick={() => setPage(page + 1)}>Next</button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <style>{`
        .extra-small { font-size: 11px; }
        .invoice-table thead th { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: #64748b; padding: 15px 10px; border: none; }
        .invoice-table tbody td { padding: 12px 10px; border-bottom: 1px solid #f1f5f9; }
        
        .invoice-icon-box { height: 35px; width: 35px; background: #eef2ff; color: #4f46e5; display: flex; align-items: center; justify-content: center; border-radius: 8px; font-size: 16px; }
        
        .status-pill { font-size: 9px; font-weight: 800; text-transform: uppercase; padding: 2px 0; display: block; }
        .status-pill.paid { color: #10b981; }
        .status-pill.unpaid { color: #f43f5e; }

        .badge-soft { padding: 4px 10px; border-radius: 6px; font-size: 10px; font-weight: 700; text-transform: uppercase; }
        .bg-success { background-color: #dcfce7 !important; color: #15803d !important; }
        .bg-warning { background-color: #fef3c7 !important; color: #b45309 !important; }

        .btn-view-invoice { background: #fff; border: 1px solid #e2e8f0; color: #475569; padding: 5px 12px; border-radius: 6px; font-size: 12px; font-weight: bold; transition: 0.2s; }
        .btn-view-invoice:hover { background: #4f46e5; color: #fff; border-color: #4f46e5; }

        .vendor-stack { line-height: 1.4; }
        .rounded-4 { border-radius: 1rem !important; }
      `}</style>
    </>
  );
}

export default AllInvoice;